/* Membership — WildQuest tier catalog + upgrade CTA.
 *
 * Live pricing from GET /api/credits/tiers so the UI always matches
 * whatever the backend/Stripe catalog reports. Checkout goes through
 * POST /api/billing/checkout with the tier's stripe_lookup key. Credit
 * packs are surfaced but currently show a "Coming soon" toast until
 * the pack integration ships.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, Star, Compass, Feather, Mountain, Loader2, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useT, useI18n } from '../i18n/I18nContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

const TIER_ICON = {
  trailhead:  Compass,
  explorer:   Feather,
  naturalist: Star,
  pathfinder: Mountain,
};

const TIER_ACCENT = {
  trailhead:  { ring: 'border-emerald-400/30', chip: 'text-emerald-200 bg-emerald-500/10', halo: 'from-emerald-500/10' },
  explorer:   { ring: 'border-white/10',       chip: 'text-white/80 bg-white/5',           halo: 'from-white/5' },
  naturalist: { ring: 'border-amber-400/50',   chip: 'text-amber-200 bg-amber-500/15',     halo: 'from-amber-500/15' },
  pathfinder: { ring: 'border-purple-400/40',  chip: 'text-purple-200 bg-purple-500/15',   halo: 'from-purple-500/15' },
};

export default function Membership() {
  const nav = useNavigate();
  const t = useT();
  const { raw } = useI18n();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [tiers, mine] = await Promise.all([
          api.get('/credits/tiers').then(r => r.data).catch(() => null),
          api.get('/credits/status').then(r => r.data).catch(() => null),
        ]);
        setData(tiers);
        setStatus(mine);
      } finally { setLoading(false); }
    })();
  }, []);

  const startCheckout = async (tier) => {
    if (!tier.stripe_lookup) return;
    setCheckoutBusy(tier.id);
    try {
      const { data } = await api.post('/billing/checkout', {
        lookup_key: tier.stripe_lookup,
        origin_url: window.location.origin,
        return_path: '/membership',
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || t('membership.checkoutFailedFallback', 'Checkout failed. Please try again.'));
      toast.error(msg);
      setCheckoutBusy(null);
    }
  };

  const packBuy = async (packId) => {
    try {
      const { data } = await api.post('/credits/purchase-pack', {
        pack_id: packId,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || t('upgrade.checkoutFailed', 'Checkout failed'));
      toast.error(msg);
    }
  };

  const currentTier = status?.tier || 'trailhead';
  const tiers = useMemo(() => (data?.tiers || []), [data]);
  const packs = useMemo(() => (data?.credit_packs || []), [data]);

  if (loading) return (
    <div className="p-8 flex items-center justify-center" data-testid="membership-loading">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="space-y-6 pb-10" data-testid="membership-root">
      <div className="flex items-center justify-between">
        <button onClick={() => nav(-1)} data-testid="membership-back"
          className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          <ArrowLeft className="w-3 h-3" /> {t('common.back', 'Back')}
        </button>
        <LanguageSwitcher compact />
      </div>

      {/* Header — WildQuest voice */}
      <header className="text-center space-y-3">
        <p className="text-[10px] uppercase tracking-[0.35em] text-emerald-300">{t('membership.chooseJourney', 'Choose your journey')}</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight">
          {t('membership.titlePrefix', 'Every explorer starts')} <span className="text-amber-300">{t('membership.titleAccent', 'somewhere')}</span>{t('membership.titleSuffix', '.')}
        </h1>
        <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto">
          {t('membership.intro', 'Discovery Credits power AI wildlife identification. Level and rank stay yours forever — earned through the wild, never bought.')}
        </p>
      </header>

      {/* Your credits summary */}
      {status?.credits && !status.credits.is_unlimited && (
        <div
          className="rounded-2xl bg-gradient-to-b from-emerald-500/10 to-transparent border border-emerald-400/25 p-4 flex items-center gap-3"
          data-testid="membership-credit-summary"
        >
          <Sparkles className="w-4 h-4 text-emerald-300" />
          <p className="text-sm flex-1">
            <span className="font-serif text-lg">{status.credits.total_remaining}</span>{' '}
            <span className="text-muted-foreground">{t('membership.creditsRemaining', 'Discovery Credits remaining')}</span>
          </p>
          <span className="text-[10px] uppercase tracking-widest text-emerald-200/70">{status.tier_label}</span>
        </div>
      )}

      {/* Tier cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {tiers.map(tier => (
          <TierCard key={tier.id} tier={tier}
            current={tier.id === currentTier}
            onSelect={() => startCheckout(tier)}
            busy={checkoutBusy === tier.id}
            t={t}
            raw={raw}
          />
        ))}
      </div>

      {/* Credit packs */}
      <section className="pt-2" data-testid="membership-packs">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300">{t('membership.packsTitle', 'Discovery Credit Packs')}</p>
            <p className="text-xs text-muted-foreground mt-1">{t('membership.packsSubtitle', 'One-off refills. Purchased credits never expire.')}</p>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60">{t('membership.instantTopUp', 'Instant top-up')}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {packs.map(pack => (
            <button key={pack.id}
              onClick={() => packBuy(pack.id)}
              data-testid={`membership-pack-${pack.id}`}
              className="rounded-2xl bg-card border border-white/5 p-3 text-left hover:border-amber-400/40 hover:bg-amber-500/5 transition"
            >
              <p className="font-serif text-2xl text-amber-300">{pack.credits}</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{t('membership.credits', 'credits')}</p>
              <p className="text-xs mt-1.5 font-semibold">${pack.price_usd.toFixed(2)}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Rank note */}
      <div className="rounded-2xl bg-card/50 border border-white/5 p-4 text-xs text-muted-foreground space-y-1.5">
        <p className="font-serif text-sm text-white/90">{t('membership.rankTitle', 'Explorer Rank is earned, never bought.')}</p>
        <p>
          {t('membership.rankBody', 'Scout → Tracker → Explorer → Naturalist → Pathfinder → Trailblazer → Master Explorer. Your rank reflects unique species, verified discoveries, biomes explored and expeditions completed. Membership never changes your rank.')}
        </p>
      </div>
    </div>
  );
}


function TierCard({ tier, current, onSelect, busy, t, raw }) {
  const Icon = TIER_ICON[tier.id] || Compass;
  const accent = TIER_ACCENT[tier.id] || TIER_ACCENT.explorer;
  const isFree = tier.id === 'trailhead';
  const isMostPopular = tier.most_popular;
  // Prefer translated tier copy; fall back to backend-provided values.
  const label    = t(`tiers.${tier.id}.label`, tier.label);
  const tagline  = t(`tiers.${tier.id}.tagline`, tier.tagline);
  const features = raw(`tiers.${tier.id}.features`);
  const featureList = Array.isArray(features) && features.length ? features : (tier.features || []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className={`relative rounded-3xl overflow-hidden bg-gradient-to-b ${accent.halo} to-transparent bg-card border ${accent.ring} p-5 space-y-3.5`}
      data-testid={`tier-card-${tier.id}`}
    >
      {isMostPopular && (
        <span className="absolute top-3 right-3 text-[9px] uppercase tracking-widest font-semibold bg-amber-400 text-black px-2 py-0.5 rounded-full">
          {t('membership.mostPopular', 'Most Popular')}
        </span>
      )}
      {current && (
        <span className="absolute top-3 right-3 text-[9px] uppercase tracking-widest font-semibold bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 px-2 py-0.5 rounded-full">
          {t('membership.yourPlan', 'Your plan')}
        </span>
      )}

      <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full ${accent.chip}`}>
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-[0.3em]">{label}</span>
      </div>

      <div>
        <p className="font-serif text-4xl leading-none">
          {isFree ? t('membership.free', 'Free') : `$${tier.price_usd.toFixed(2)}`}
          {!isFree && <span className="text-sm text-muted-foreground align-top ml-1">{t('membership.perMonthShort', '/mo')}</span>}
        </p>
        <p className="text-[11px] text-muted-foreground mt-1">{tagline}</p>
      </div>

      <div className="space-y-1.5 text-xs">
        {featureList.map((f, i) => (
          <div key={i} className="flex items-start gap-2">
            <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-400 shrink-0" strokeWidth={2.5} />
            <span className="text-white/85">{f}</span>
          </div>
        ))}
      </div>

      {isFree ? (
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground pt-2">
          {current ? t('membership.onTrailhead', "You're on Trailhead") : t('membership.freeToTryPill', 'Free to try · No credit card required')}
        </p>
      ) : (
        <button
          onClick={onSelect}
          disabled={busy || current}
          data-testid={`tier-select-${tier.id}`}
          className={`w-full mt-1 px-4 py-3 rounded-full text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2 transition ${
            current ? 'bg-white/5 text-white/40 cursor-not-allowed'
                    : isMostPopular ? 'bg-amber-400 text-black hover:bg-amber-300'
                                    : 'bg-emerald-500 text-black hover:bg-emerald-400'
          }`}
        >
          {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('membership.redirecting', 'Redirecting…')}</>
                : current ? t('membership.currentPlan', 'Current plan') : t('membership.chooseTier', 'Choose {tier}', { tier: label })}
        </button>
      )}
    </motion.div>
  );
}
